// Shamelessly stolen from https://usehooks.com/useAsync/

import { useCallback, useEffect, useMemo, useState } from 'react'

import type { AxiosError, AxiosResponse } from 'axios'

const pendingSet = new Set()

type PromiseFunction<T> = (...parameters: any[]) => Promise<AxiosResponse<T>>

type AxiosAsyncStatus = 'idle' | 'pending' | 'success' | 'error'

const useAxiosAsync = <F extends PromiseFunction<any>>(
  asyncFunction: F,
  swr?: boolean,
  supportsAbortToken?: boolean,
) => {
  type FunctionParameterType = Parameters<F>
  type FunctionParameterReturnType = Awaited<ReturnType<F>>

  const [status, setStatus] = useState<AxiosAsyncStatus>('idle')
  const [value, setValue] = useState<FunctionParameterReturnType['data']>()
  const [error, setError] = useState<AxiosError>()

  const functionIdentifier = useMemo(() => asyncFunction.toString(), [asyncFunction])
  const isLoading = useMemo(() => {
    return status === 'pending'
  }, [status])

  // Effects
  // ------------------------------------------------------------------------- /
  // Clear pending function on unmount
  useEffect(() => {
    return () => {
      pendingSet.delete(functionIdentifier)
    }
  }, [functionIdentifier])

  const execute = useCallback(
    (...parameters: FunctionParameterType) => {
      if (supportsAbortToken) {
        pendingSet.delete(functionIdentifier)
      }

      // Skip if still pending
      if (pendingSet.has(functionIdentifier)) {
        return
      }

      pendingSet.add(functionIdentifier)
      setStatus('pending')

      if (!swr) {
        setValue(undefined)
        setError(undefined)
      }

      return asyncFunction(...parameters)
        .then((response) => {
          if (response) {
            setValue(response.data)
            setStatus('success')
          }
          return response
        })
        .catch((error: AxiosError) => {
          if (error.code !== 'ERR_CANCELED') {
            setError(error)
            setStatus('error')
          }
        })
        .finally(() => {
          pendingSet.delete(functionIdentifier)
        })
    },
    [asyncFunction, swr, supportsAbortToken],
  )

  function reset() {
    setStatus('idle')
    setValue(undefined)
    setError(undefined)
  }

  return { execute, reset, value, error, status, isLoading }
}

export type { AxiosAsyncStatus }
export { useAxiosAsync }
